import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Receipt, ArrowUpRight, Search } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./OpsDashboard";

const $ = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUSES = ["", "draft", "sent", "paid", "overdue", "cancelled"];

export default function OpsInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/invoices");
        setInvoices(data);
      } catch {
        toast.error("Failed to load invoices");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = invoices.filter((i) => {
    if (filter && i.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${i.number || ""} ${i.customer?.full_name || ""} ${i.customer?.email || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Cobros</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-8">Facturas</h1>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por número, cliente, email…"
            className="input-dark pl-10"
          />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-dark sm:w-56 cursor-pointer">
          {STATUSES.map((s) => <option key={s} value={s}>{s === "" ? "Todos los estados" : s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center text-white/55">
          {invoices.length === 0 ? "Aún no hay facturas. Genera una desde un pedido." : "Ninguna factura coincide con el filtro."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => (
            <Link
              key={i.id}
              to={`/admin/ops/invoices/${i.id}`}
              className="block bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/50 transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Receipt className="w-4 h-4 text-[#D4AF37]" />
                    <h3 className="font-display text-base sm:text-lg font-bold">{i.number}</h3>
                    <StatusPill value={i.status} />
                  </div>
                  <div className="mt-2 text-xs text-white/55 truncate">
                    {i.customer?.full_name || "—"} · emitida {new Date(i.issued_at).toLocaleDateString()}
                    {i.due_date && ` · vence ${new Date(i.due_date).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-display text-lg font-bold text-[#D4AF37]">{$(i.total)}</div>
                    {i.tax_rate > 0 && <div className="text-[10px] text-white/40">incluye {i.tax_rate}% impuesto</div>}
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
